import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Grid, Card, CardContent, LinearProgress, Avatar, AvatarGroup, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, InputAdornment } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import Loading from '../components/Loading';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
      map.flyTo(e.latlng, map.getZoom());
    },
  });

  return position === null ? null : (
    <Marker position={position}></Marker>
  );
}

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [newProject, setNewProject] = useState({ 
      name: '', quote_ref: '', start_date: '', due_date: '', client_id: '', country_id: '', division_id: '',
      description: '', progress: 0, priority: 'Medium', budget: 0, spent: 0,
      latitude: '', longitude: ''
  });
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const res = await axios.get(`${API_URL}/projects?${params.toString()}`, { headers });
      setProjects(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeta = async () => {
      try {
        const [cRes, coRes, dRes] = await Promise.all([
            axios.get(`${API_URL}/clients`, { headers }),
            axios.get(`${API_URL}/setup/countries`, { headers }),
            axios.get(`${API_URL}/setup/divisions`, { headers })
        ]);
        setClients(cRes.data);
        setCountries(coRes.data);
        setDivisions(dRes.data);
      } catch (e) {
          console.error(e);
      }
  }

  useEffect(() => {
    fetchProjects();
    fetchMeta();
  }, [search]);

  if (loading) return <Loading message="Loading Projects..." />;

  const handleCreate = async () => {
    setCreateLoading(true);
    try {
      await axios.post(`${API_URL}/projects`, newProject, { headers });
      setOpen(false);
      fetchProjects();
    } catch (err) {
      alert('Error creating project');
    } finally {
      setCreateLoading(false);
    }
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Active': return 'primary';
          case 'In Progress': return 'primary';
          case 'Completed': return 'success';
          case 'At Risk': return 'warning';
          case 'Overdue': return 'warning';
          case 'On Hold': return 'error';
          default: return 'default';
      }
  };

  const getPriorityColor = (priority) => {
      switch(priority) {
          case 'High': return '#DC3545';
          case 'Medium': return '#FFB020';
          case 'Low': return '#00C292';
          default: return '#6C757D';
      }
  };

  const filteredProjects = projects.filter(p => {
      if (filter === 'All') return true;
      if (filter === 'Active') return p.status === 'In Progress' || p.status === 'Active';
      if (filter === 'At Risk') return p.status === 'Overdue' || p.status === 'At Risk';
      return p.status === filter;
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
            {['All', 'Active', 'Completed', 'At Risk', 'On Hold'].map(f => (
                <Chip 
                    key={f} 
                    label={f} 
                    onClick={() => setFilter(f)} 
                    color={filter === f ? 'primary' : 'default'} 
                    variant={filter === f ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 'bold' }}
                />
            ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField 
                placeholder="Search projects..." 
                size="small" 
                value={search} 
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    sx: { borderRadius: 5, bgcolor: 'white' }
                }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ borderRadius: 5 }}>Add Project</Button>
          
        </Box>
      </Box>

      {/* Project Grid */}
      <Grid container spacing={3}>
        {filteredProjects.map(p => (
            <Grid item xs={12} md={4} lg={3} key={p.id}>
                <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 1, cursor: 'pointer', transition: '0.3s', '&:hover': { boxShadow: 4 } }} onClick={() => navigate(`/projects/${p.id}`)}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.client}</Typography>
                                {p.quote_ref && <Typography variant="caption" color="textSecondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Quote: {p.quote_ref}</Typography>}
                            </Box>
                            <Chip label={p.status} size="small" color={getStatusColor(p.status)} sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2, height: 40, overflow: 'hidden' }}>
                            {p.description || "No description provided."}
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="textSecondary">Progress</Typography>
                                <Typography variant="caption" fontWeight="bold">{p.progress}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={p.progress} sx={{ height: 6, borderRadius: 3 }} />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="textSecondary">Due: {p.due_date}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(p.priority) }} />
                                <Typography variant="caption" color="textSecondary">{p.priority} Priority</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #eee' }}>
                            <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                                {p.team && p.team.map((member, index) => (
                                    <Avatar key={index} alt={member.name} title={`${member.name} (${member.role || 'Member'})`} sx={{ bgcolor: 'primary.main' }}>
                                        {member.name ? member.name.charAt(0) : '?'}
                                    </Avatar>
                                ))}
                                {(!p.team || p.team.length === 0) && (
                                    <Avatar sx={{ bgcolor: 'grey.300' }}>?</Avatar>
                                )}
                            </AvatarGroup>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" display="block" fontWeight="bold">${p.spent?.toLocaleString()}</Typography>
                                <Typography variant="caption" color="textSecondary">of ${p.budget?.toLocaleString()}</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        ))}
      </Grid>

      {/* Add Project Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Project</DialogTitle>
        <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField label="Name" fullWidth value={newProject.name} onChange={e => setNewProject({...newProject, name: e.target.value})} />
                <TextField label="Description" multiline rows={3} fullWidth value={newProject.description} onChange={e => setNewProject({...newProject, description: e.target.value})} />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField label="Quote Ref" fullWidth value={newProject.quote_ref} onChange={e => setNewProject({...newProject, quote_ref: e.target.value})} />
                    <TextField label="Budget ($)" type="number" fullWidth value={newProject.budget} onChange={e => setNewProject({...newProject, budget: e.target.value})} />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newProject.start_date} onChange={e => setNewProject({...newProject, start_date: e.target.value})} />
                    <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={newProject.due_date} onChange={e => setNewProject({...newProject, due_date: e.target.value})} />
                </Box>

                <Typography variant="subtitle2" gutterBottom>Location (Click on map to select)</Typography>
                <Box sx={{ height: 300, width: '100%', mb: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                    <MapContainer center={[20, 0]} zoom={2} style={{ height: '100%', width: '100%' }}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        <LocationMarker 
                            position={newProject.latitude && newProject.longitude ? { lat: newProject.latitude, lng: newProject.longitude } : null} 
                            setPosition={(latlng) => setNewProject({ ...newProject, latitude: latlng.lat, longitude: latlng.lng })}
                        />
                    </MapContainer>
                </Box>

                <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select value={newProject.priority} label="Priority" onChange={e => setNewProject({...newProject, priority: e.target.value})}>
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                    </Select>
                </FormControl>
                
                <FormControl fullWidth>
                    <InputLabel>Client</InputLabel>
                    <Select value={newProject.client_id} label="Client" onChange={e => setNewProject({...newProject, client_id: e.target.value})}>
                        {clients.map(c => (
                            <MenuItem key={c.id} value={c.id} disabled={c.status !== 'Approved'}>
                                {c.name} {c.status !== 'Approved' && `(${c.status || 'Pending'})`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Country</InputLabel>
                        <Select value={newProject.country_id} label="Country" onChange={e => setNewProject({...newProject, country_id: e.target.value})}>
                            {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Division</InputLabel>
                        <Select value={newProject.division_id} label="Division" onChange={e => setNewProject({...newProject, division_id: e.target.value})}>
                            {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
              {createLoading ? 'Creating...' : 'Create'}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Projects;
